import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useFlags, usePassVouchers } from '../api/hooks';
import { Loader } from '../components/StateViews';
import { BackIcon } from '../components/icons';
import { SheetClose } from '../components/SheetClose';
import { backButton, haptics } from '../telegram';
import { voucherName } from './PassScreen';

const VOUCHER_VALUE: Record<string, string> = { drink: '1 ₽', entry: '1 вход', reentry: 'RE ×1' };
const VOUCHER_DESC: Record<string, string> = {
  drink: 'Любой напиток за 1 ₽ на игре',
  entry: 'Бесплатный вход на турнир',
  reentry: 'Бесплатный ре-энтри в турнире',
};

/** Ваучер по спеке: тёмная левая часть 74px с иконкой и значением, вырезы,
 *  «ВАУЧЕР · УР. N», статус-пилюля, код и «Показать» (QR); погашенный —
 *  grayscale со штампом «ПОГАШЕН». */
function VoucherCard({
  v,
  onShow,
}: {
  v: { code: string; type: string; level: number | null; status: string };
  onShow: () => void;
}) {
  const used = v.status === 'redeemed';
  return (
    <div className={`bpv${used ? ' bpv--used' : ''}`}>
      <span className="bpv-left">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
          <path d="M4 8a2 2 0 0 0 2-2h12a2 2 0 0 0 2 2v3a2 2 0 0 0 0 2v3a2 2 0 0 0-2 2H6a2 2 0 0 0-2-2v-3a2 2 0 0 0 0-2z" />
        </svg>
        <b className="mono">{VOUCHER_VALUE[v.type] ?? '✦'}</b>
      </span>
      <span className="bpv-cut bpv-cut--t" aria-hidden />
      <span className="bpv-cut bpv-cut--b" aria-hidden />
      <span className="bpv-main">
        <span className="bpv-top">
          <span className="lbl mono">ВАУЧЕР{v.level ? ` · УР. ${v.level}` : ''}</span>
          <span className={`pill${used ? ' used' : ''}`}>{used ? 'Погашен' : 'Активен'}</span>
        </span>
        <span className="nm">{voucherName(v.type)}</span>
        <span className="desc">{VOUCHER_DESC[v.type] ?? 'Покажи код на стойке клуба'}</span>
        <span className="bpv-bottom">
          <span className="code mono">{v.code}</span>
          {!used && (
            <button className="show" onClick={onShow}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h3M20 14v3M14 20h6" />
              </svg>
              Показать
            </button>
          )}
        </span>
      </span>
      {used && <span className="bpv-stamp mono">ПОГАШЕН</span>}
    </div>
  );
}

function VoucherQrSheet({ code, onClose }: { code: string; onClose: () => void }) {
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    void QRCode.toDataURL(code, { margin: 1, width: 220 }).then(setQr);
  }, [code]);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet bp-order" onClick={(e) => e.stopPropagation()}>
        <SheetClose onClose={onClose} />
        <h3>Покажи на стойке</h3>
        <div className="bp-order-code mono">{code}</div>
        {qr && <img className="bp-order-qr" src={qr} alt="QR" />}
      </div>
    </div>
  );
}

/** Экран ваучеров Pass+ — вынесен с 6.1 (по макету на дорожке их нет),
 *  вход из блока профиля 6.0b. */
export function VouchersScreen() {
  const flags = useFlags();
  const enabled = Boolean(flags.data?.pass);
  const vouchers = usePassVouchers(enabled);
  const navigate = useNavigate();
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (flags.data && !enabled) return <Navigate to="/" replace />;
  if (!flags.data || vouchers.isLoading) return <Loader />;
  const list = vouchers.data ?? [];

  return (
    <div className="bp-screen">
      <div className="subhead">
        <button
          className="iconbtn"
          onClick={() => {
            haptics.tap();
            navigate(-1);
          }}
          aria-label="Назад"
        >
          <BackIcon />
        </button>
        <div className="title">Ваучеры</div>
      </div>
      {list.length === 0 ? (
        <div className="q-empty">Ваучеры появятся на дорожке Straddle Pass+</div>
      ) : (
        <div className="bp-vouchers">
          {list.map((v) => (
            <VoucherCard key={v.code} v={v} onShow={() => setQr(v.code)} />
          ))}
        </div>
      )}
      {qr && <VoucherQrSheet code={qr} onClose={() => setQr(null)} />}
    </div>
  );
}
